/**
 * component-modal.js
 *
 * Generic modal open/close behaviour.
 * Trigger open:  data-modal-open="<modal_id>"  on any element.
 * Trigger close: data-modal-close               on overlay or close button inside modal.
 *
 * Usage:
 *   <button data-modal-open="member-rolf">View Profile</button>
 */

class ModalComponent {
  constructor(modalEl) {
    this.modal = modalEl;
    this.focusableSelectors =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    document.body.appendChild(this.modal);
    this._bindEvents();
  }

  open() {
    this.modal.removeAttribute('hidden'); // make it participates in layout
    // Force a reflow so the browser registers the initial state before adding the class
    this.modal.getBoundingClientRect();
    this.modal.classList.add('modal--is-visible');

    document.body.style.overflow = 'hidden';
    this._returnFocusEl = document.activeElement;
    this._trapFocus();
  }

  close() {
    this.modal.classList.remove('modal--is-visible');

    // Wait for the CSS transition to finish before hiding from a11y tree
    this.modal.addEventListener(
      'transitionend',
      () => {
        this.modal.setAttribute('hidden', '');
        document.body.style.overflow = '';
        if (this._returnFocusEl) {
          this._returnFocusEl.focus();
        }
        this.modal.dispatchEvent(new CustomEvent('modal-close'));
      },
      { once: true },
    );
  }

  _bindEvents() {
    // Close on overlay / close button click
    this.modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-modal-close]')) {
        this.close();
      }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.modal.hidden) {
        this.close();
      }
    });
  }

  _trapFocus() {
    const focusableEls = Array.from(
      this.modal.querySelectorAll(this.focusableSelectors),
    );

    if (!focusableEls.length) return;

    // Move focus into modal
    focusableEls[0].focus();

    this.modal.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Tab') return;

        const first = focusableEls[0];
        const last = focusableEls[focusableEls.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      },
      { signal: this._abortController?.signal },
    );
  }
}

// Registry: modalId → ModalComponent instance
const modalRegistry = new Map();

function getOrCreateModal(modalId) {
  if (modalRegistry.has(modalId)) return modalRegistry.get(modalId);

  const el = document.getElementById(`Modal-${modalId}`);
  if (!el) return null;

  const instance = new ModalComponent(el);
  modalRegistry.set(modalId, instance);
  return instance;
}

// Global click delegation for [data-modal-open]
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-modal-open]');
  if (!trigger) return;

  const modalId = trigger.dataset.modalOpen;
  const modal = getOrCreateModal(modalId);
  if (modal) modal.open();
});
